import { useQuery } from '@apollo/client/react';
import { Banner, type BannerVariant, FlatButton, stenaTimes } from '@stenajs-webui/elements';
import { graphql } from '../../gql';
import { SeverityLevel } from '../../gql/graphql';
import { TEN_MINUTES_IN_MS } from '../../services/frontend-out-of-date/frontendOutOfDateSaga';
import { trackEvent } from '../tracking/trackerService';
import { usePersistedStorageState } from './usePersistedStorageState';

const maintenanceMessageQuery = graphql(/* GraphQL */ `
  query MaintenanceMessageQuery {
    viewer {
      maintenanceMessages {
        id
        title
        description
        dismissible
        severity
      }
    }
  }
`);

function getVariantFromMessageSeverity(message: SeverityLevel): BannerVariant {
  switch (message) {
    case SeverityLevel.Info:
      return 'info';
    case SeverityLevel.Error:
      return 'error';
    case SeverityLevel.Warning:
      return 'warning';
  }
}

export const MaintenanceMessageBanner = () => {
  const [hiddenIds, setHiddenIds] = usePersistedStorageState<string[]>(
    'MAINTENANCE_MESSAGE_IDS',
    [],
  );
  const { data } = useQuery(maintenanceMessageQuery, {
    pollInterval: TEN_MINUTES_IN_MS,
  });

  const ids = hiddenIds ?? [];
  const messages =
    data?.viewer.maintenanceMessages.filter(message => !ids.includes(message.id)) ?? [];

  if (messages.length < 1) {
    return null;
  }

  return (
    <>
      {messages.map(message => {
        const trackerLabel = message.title ?? message.description;

        return (
          <Banner
            key={message.id}
            variant={getVariantFromMessageSeverity(message.severity)}
            headerText={message.title ?? undefined}
            text={message.description}
            contentRight={
              message.dismissible ? (
                <FlatButton
                  leftIcon={stenaTimes}
                  onClick={() => {
                    setHiddenIds([...ids, message.id]);
                    trackEvent({
                      category: 'General',
                      action: 'Dismiss maintenance message',
                      label: trackerLabel,
                    });
                  }}
                />
              ) : null
            }
          />
        );
      })}
    </>
  );
};
